import { captureException } from "@sentry/react";
import axios from "axios";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";
import { ERROR_MSG } from "@utils/enum";
import { trackBulkSearch } from "../analytics";
import { BULK_LOGS_PATH, FILE_TOO_LARGE_MSG, INVALID_FILE_TYPE_MSG } from "../constants";
import { downloadBulkSampleFile, getApiErrorMessage, uploadBulkCompanyFile } from "../services";
import { isAcceptedFileType, isWithinSizeLimit } from "../utils";
import FileDropzone from "./FileDropzone";
import UploadedFileRow from "./UploadedFileRow";
import type { BulkSearchDispatch, BulkValidationError, UploadPhase, UploadProgress } from "../types";

const INITIAL_PROGRESS: UploadProgress = { percent: 0, secondsLeft: null };

const BulkUploadPanel = () => {
  const history = useHistory();
  const dispatch = useDispatch<BulkSearchDispatch>();
  const bulkSearchLogsCount = useSelector((state: { sidebarCount: { countDetails?: { bulk_search_logs_count?: number } } }) => state.sidebarCount.countDetails?.bulk_search_logs_count || 0);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<UploadPhase>("EMPTY");
  const [progress, setProgress] = useState<UploadProgress>(INITIAL_PROGRESS);
  const [validationErrors, setValidationErrors] = useState<BulkValidationError[]>([]);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [runningJob, setRunningJob] = useState<{ searchId: string; totalRecords: number; message: string } | null>(null);
  const cancelSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const uploadStartRef = useRef<number>(0);

  const resetPanel = () => {
    cancelSourceRef.current?.cancel();
    cancelSourceRef.current = null;
    setFile(null);
    setPhase("EMPTY");
    setProgress(INITIAL_PROGRESS);
    setValidationErrors([]);
    setSearchId(null);
    setTotalRecords(0);
    setRunningJob(null);
  };

  const handleProgress = (loaded: number, total: number) => {
    if (!total) return;
    const percent = Math.min(100, (loaded / total) * 100);
    const elapsedSeconds = (Date.now() - uploadStartRef.current) / 1000;
    const rate = elapsedSeconds > 0 ? loaded / elapsedSeconds : 0;
    setProgress({ percent, secondsLeft: rate > 0 ? (total - loaded) / rate : null });
  };

  const handleFileSelected = async (selected: File) => {
    if (!isAcceptedFileType(selected)) {
      toast.error(INVALID_FILE_TYPE_MSG);
      return;
    }
    if (!isWithinSizeLimit(selected)) {
      toast.error(FILE_TOO_LARGE_MSG);
      return;
    }

    setFile(selected);
    setValidationErrors([]);
    setRunningJob(null);
    setPhase("UPLOADING");
    setProgress(INITIAL_PROGRESS);
    uploadStartRef.current = Date.now();
    cancelSourceRef.current = axios.CancelToken.source();

    try {
      const response = await uploadBulkCompanyFile(selected, {
        onProgress: handleProgress,
        cancelToken: cancelSourceRef.current.token,
      });
      cancelSourceRef.current = null;

      const pendingError = response.errors?.find((error) => error.code === "JOB_ALREADY_RUNNING");

      if (pendingError && response.search_id) {
        setRunningJob({ searchId: response.search_id, totalRecords: response.total_records ?? 0, message: pendingError.message });
        setPhase("EMPTY");
        setFile(null);
        return;
      }

      if (response.errors?.length) {
        setValidationErrors(response.errors);
        setPhase("EMPTY");
        setFile(null);
        return;
      }

      setProgress({ percent: 100, secondsLeft: null });
      // The new search adds a log, so refresh the counts that gate "View logs".
      dispatch(getSidebarCountData());
      setSearchId(response.search_id);
      setTotalRecords(response.total_records ?? 0);
      setPhase("UPLOADED");
      trackBulkSearch("companyBulkSearchFileUploaded", { search_id: response.search_id, total_records: response.total_records ?? 0, file_size: selected.size, file_extension: selected.name.split(".").pop()?.toLowerCase() });
    } catch (error) {
      if (axios.isCancel?.(error)) return;
      captureException(error);
      toast.error(getApiErrorMessage(error, ERROR_MSG));
      resetPanel();
    }
  };

  const handleSubmit = () => {
    if (!searchId) return;
    trackBulkSearch("companyBulkSearchSubmitted", { search_id: searchId, total_records: totalRecords });
    setPhase("SUBMITTING");
    history.push({ pathname: `/company-search/bulk/${searchId}`, state: { totalRecords } });
  };

  const handleDownloadSample = async () => {
    trackBulkSearch("companyBulkSearchSampleDownloaded");
    try {
      await downloadBulkSampleFile();
    } catch (error) {
      captureException(error);
      toast.error(error instanceof Error && error.message ? error.message : ERROR_MSG);
    }
  };

  return (
    <div className="tw-mx-auto tw-w-[623px] tw-rounded-[20px] tw-bg-white tw-p-8 tw-shadow-autoSuggest">
      <div className="tw-flex tw-h-8 tw-items-center tw-justify-between">
        <div className="tw-text-sm tw-font-semibold tw-leading-5 tw-text-uploader-title">File Upload</div>
        {bulkSearchLogsCount > 0 && (
          <button
            type="button"
            onClick={() => {
              trackBulkSearch("companyBulkSearchLogsOpened", { logs_count: bulkSearchLogsCount });
              history.push(BULK_LOGS_PATH);
            }}
            className="tw-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-[#d0d5dd] tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-leading-5 tw-text-[#101828] tw-shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:tw-bg-gray-50">
            View bulk search history
            <span className="material-symbols-outlined tw-text-xl tw-leading-none tw-font-semibold">arrow_right_alt</span>
          </button>
        )}
      </div>
      <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-text-sm tw-leading-5">
        <span className="tw-text-uploader-subtitle">Choose a file with list of company names</span>
        <button type="button" onClick={handleDownloadSample} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-[#5a1ce2] hover:tw-underline">
          Download sample
        </button>
      </div>

      <div className="tw-mt-8">
        {phase === "EMPTY" && <FileDropzone onFileSelected={handleFileSelected} />}
        {file && phase !== "EMPTY" && (
          <div className="tw-rounded-2xl tw-border tw-border-uploader-border tw-bg-white tw-p-8">
            <UploadedFileRow fileName={file.name} fileSize={file.size} isUploading={phase === "UPLOADING"} progress={progress} onRemove={resetPanel} />
          </div>
        )}
      </div>

      {(phase === "UPLOADED" || phase === "SUBMITTING") && (
        <button type="button" onClick={handleSubmit} disabled={phase === "SUBMITTING"} className="tw-mt-4 tw-h-9 tw-rounded-lg tw-border-none tw-bg-primary-purple-600 tw-px-4 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-purple-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-50">
          {phase === "SUBMITTING" ? "Please wait…" : "Submit"}
        </button>
      )}

      {runningJob && (
        <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-warning-300 tw-bg-warning-25 tw-p-3">
          <div className="tw-text-sm tw-font-medium tw-text-gray-900">This file was not submitted</div>
          <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-gray-700">{runningJob.message} Upload it again once that search finishes.</p>
          <button type="button" onClick={() => history.push({ pathname: `/company-search/bulk/${runningJob.searchId}`, state: { totalRecords: runningJob.totalRecords } })} className="tw-mt-2 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-600 hover:tw-underline">
            View the running search
          </button>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="tw-mt-4 tw-rounded-lg tw-border tw-border-error-200 tw-bg-error-50 tw-p-3">
          <div className="tw-text-sm tw-font-medium tw-text-error-600">We couldn&apos;t process this file</div>
          <ul className="tw-mb-0 tw-mt-2 tw-list-disc tw-pl-5 tw-text-sm tw-text-gray-700">
            {validationErrors.map((error) => (
              <li key={`${error.code}-${error.row ?? "file"}-${error.message}`}>
                {error.message}
                {typeof error.row === "number" ? ` (row ${error.row})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BulkUploadPanel;
